import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  UserRound, 
  Receipt, 
  History, 
  Headphones, 
  Wallet2, 
  ArrowUpDown, 
  Settings, 
  LogOut,
  X
} from 'lucide-react';
import HelpIcon from '@/components/icons/HelpIcon';
import { getUID, getSocket } from '@/utils';
import { C } from '@/utils/constants';
import Cookies from 'js-cookie';
import LogoutModal from './LogoutModal';
import styles from './ProfileMenuMobile.module.css';

const DEFAULT_AVATAR_PATH = '/assets/images/profile-jk.webp';

/**
 * ProfileMenuMobile Component
 * Mobile-specific bottom sheet menu for profile actions
 * Opens as a full bottom sheet modal on mobile devices
 */
const ProfileMenuMobile = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [avatarUrl, setAvatarUrl] = useState(DEFAULT_AVATAR_PATH);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  useEffect(() => {
    const storedAvatar = localStorage.getItem('avatarUrl');
    if (storedAvatar) {
      setAvatarUrl(storedAvatar);
    }
  }, []);

  useEffect(() => {
    // Prevent body scroll when menu is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleLogoutClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Open modal first, then close menu
    setShowLogoutModal(true);
    // Close menu after a brief delay to allow modal to render
    setTimeout(() => {
      onClose();
    }, 50);
  };

  const handleLogoutConfirm = () => {
    // Emit logout to socket if available
    const socket = getSocket();
    if (socket && socket.connected) {
      try {
        socket.emit(C.LOGOUT_USER);
      } catch (error) {
        console.error('Error emitting logout:', error);
      }
    }

    // Clear authentication
    Cookies.remove('token');
    Cookies.remove('uid');
    Cookies.remove('session');
    Cookies.remove('auth');
    localStorage.clear();
    
    // Close modals
    setShowLogoutModal(false);
    onClose();
    
    // Redirect to home
    navigate('/');
    
    // Reload page to reset state
    window.location.reload();
  };

  const handleMenuItemClick = () => {
    onClose();
  };

  const getValidAvatarUrl = (url) => {
    if (!url || typeof url !== 'string') return DEFAULT_AVATAR_PATH;
    if (url.startsWith('https') || url.startsWith('http') || url.startsWith('localhost')) {
      return DEFAULT_AVATAR_PATH;
    }
    return DEFAULT_AVATAR_PATH;
  };

  const menuItems = [
    {
      icon: UserRound,
      label: 'User Information',
      to: '/modal?mode=profile',
      action: handleMenuItemClick,
    },
    {
      icon: Receipt,
      label: 'Transactions',
      to: '/modal?mode=bethistory&tab=transactions',
      action: handleMenuItemClick,
    },
    {
      icon: History,
      label: 'Bet History',
      to: '/modal?mode=bethistory&tab=bets',
      action: handleMenuItemClick,
    },
    // {
    //   icon: Headphones,
    //   label: 'Live Support',
    //   to: '/support',
    //   action: handleMenuItemClick,
    // },
    {
      icon: Wallet2,
      label: 'Wallet',
      to: '/wallet?mode=deposit',
      action: handleMenuItemClick,
    },
    {
      icon: ArrowUpDown,
      label: 'Swap',
      to: '/wallet?mode=swap',
      action: handleMenuItemClick,
    },
    {
      icon: Settings,
      label: 'Global Settings',
      to: '/settings',
      action: handleMenuItemClick,
    },
    {
      icon: HelpIcon,
      label: 'Help',
      to: '/provably-fair',
      action: handleMenuItemClick,
    },
  ];

  return (
    <>
      {/* Profile Menu - Only render when isOpen */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className={styles.backdrop}
            onClick={onClose}
            aria-hidden="true"
          />

          {/* Compact Popover */}
          <div className={`${styles.popover} ${isOpen ? styles.popoverOpen : ''}`}>
            {/* Menu List */}
            <div className={styles.menuList}>
              {menuItems.map((item, index) => {
                const Icon = item.icon;
                const isCustomIcon = item.label === 'Help';
                return (
                  <Link
                    key={index}
                    to={item.to}
                    className={styles.menuItem}
                    onClick={item.action}
                  >
                    <div className={styles.iconWrapper}>
                      {isCustomIcon ? (
                        <Icon size="16px" />
                      ) : (
                        <Icon size={16} />
                      )}
                    </div>
                    <span className={styles.menuText}>{item.label}</span>
                  </Link>
                );
              })}

              <div className={styles.divider}></div>

              <button
                className={styles.menuItem}
                onClick={handleLogoutClick}
              >
                <div className={styles.iconWrapper}>
                  <LogOut size={16} />
                </div>
                <span className={styles.menuText}>Logout</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Logout Confirmation Modal - Always render, controlled by showLogoutModal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogoutConfirm}
      />
    </>
  );
};

export default ProfileMenuMobile;
